import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollText } from 'lucide-react';

interface SystemLog {
  id: string;
  level: string;
  message: string;
  category: string;
  timestamp: number;
  metadata?: any;
}

function SystemLogs() {
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/logs`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch system logs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getLevelBadgeColor = (level: string) => {
    const colors: Record<string, string> = {
      DEBUG: 'bg-gray-500',
      INFO: 'bg-blue-500',
      WARNING: 'bg-yellow-500',
      ERROR: 'bg-red-500',
      CRITICAL: 'bg-purple-500',
    };
    return colors[level] || 'bg-gray-500';
  };

  const getCategoryBadgeColor = (category: string) => {
    const colors: Record<string, string> = {
      SYSTEM: 'bg-blue-500',
      APPLICATION: 'bg-green-500',
      DATABASE: 'bg-purple-500',
      SECURITY: 'bg-red-500',
      PERFORMANCE: 'bg-orange-500',
    };
    return colors[category] || 'bg-gray-500';
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <ScrollText className="h-8 w-8" />
            System Logs
          </h2>
          <p className="text-muted-foreground mt-2">
            System-wide logging and monitoring
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Logs</CardTitle>
          <CardDescription>
            {logs.length} log entr{logs.length !== 1 ? 'ies' : 'y'} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Level</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Message</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    No logs found
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="font-mono text-xs">
                      {new Date(log.timestamp * 1000).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge className={getLevelBadgeColor(log.level)}>
                        {log.level}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={getCategoryBadgeColor(log.category)}>
                        {log.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-2xl truncate">{log.message}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export default SystemLogs;
